import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { EditorContent } from '@tiptap/react';
import { 
    Maximize2, 
    Type,
    ChevronLeft,
    CheckCircle2,
    Cloud
} from 'lucide-react';

const FocusModeOverlay = ({ editor, onClose }) => {
    const [saveStatus, setSaveStatus] = useState('saved'); // 'saved', 'dirty', 'saving'
    const [wordCount, setWordCount] = useState(0);

    useEffect(() => {
        if (!editor) return;

        // Initial word count
        const initialText = editor.getText();
        setWordCount(initialText.trim() ? initialText.trim().split(/\s+/).length : 0);

        const handleUpdate = () => {
            setSaveStatus('dirty');
            const text = editor.getText();
            // More robust word counting regex
            const count = text.trim() ? text.trim().split(/\s+/).length : 0;
            setWordCount(count);
        };

        editor.on('update', handleUpdate);

        return () => {
            editor.off('update', handleUpdate);
        };
    }, [editor]);

    // Simulate auto-save transition: dirty -> saving -> saved
    useEffect(() => {
        if (saveStatus === 'dirty') {
            const timer = setTimeout(() => {
                setSaveStatus('saving');
                const saveTimer = setTimeout(() => {
                    setSaveStatus('saved');
                }, 1000);
                return () => clearTimeout(saveTimer);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [saveStatus]);

    if (!editor) return null;

    // Use portal to escape any parent modal overflow constraints
    return ReactDOM.createPortal(
        <div className="fixed top-16 left-0 right-0 bottom-0 z-[150] bg-slate-50/80 backdrop-blur-md overflow-y-auto overflow-x-hidden animate-in fade-in slide-in-from-bottom-8 flex flex-col scroll-smooth">
            {/* Redesigned High-Density Header */}
            <header className="sticky top-0 z-50 bg-white border-b border-slate-100 px-6 h-16 flex items-center justify-between shrink-0 shadow-sm transition-all duration-500">
                <div className="flex items-center gap-6">
                    <button 
                        onClick={onClose}
                        className="group flex items-center gap-3 text-slate-400 hover:text-[#18216D] transition-all"
                    >
                        <div className="w-10 h-10 rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-center group-hover:bg-indigo-50 group-hover:border-indigo-100 shadow-sm transition-all">
                            <ChevronLeft className="w-5 h-5 transition-transform group-hover:-translate-x-0.5" />
                        </div>
                        <div className="flex flex-col text-left">
                            <span className="text-[10px] font-black uppercase tracking-[0.2em] leading-none mb-1">Exit Focus</span>
                            <span className="text-[8px] font-bold text-slate-300 uppercase tracking-widest leading-none">Normal View</span>
                        </div>
                    </button>

                    <div className="w-[1px] h-8 bg-slate-100 mx-2" />

                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <span className="px-2 py-0.5 bg-indigo-50 text-indigo-500 text-[8px] font-black uppercase tracking-widest rounded-md border border-indigo-100/50">
                                Focus Mode
                            </span>
                            <span className="text-slate-300 text-[8px]">/</span>
                            <span className="text-slate-400 text-[8px] font-bold uppercase tracking-widest">Writing Canvas</span>
                        </div>
                        <h2 className="text-sm font-black text-[#18216D] tracking-tight uppercase leading-none">Lesson Content Creation</h2>
                    </div>
                </div>

                <div className="flex items-center gap-8">
                    {/* Compact Stats Group */}
                    <div className="hidden lg:flex items-center gap-6">
                        <div className="flex flex-col items-end">
                            <div className="flex items-center gap-1.5 text-slate-400">
                                <Type size={12} className="opacity-50" />
                                <span className="text-[10px] font-black text-[#18216D]">{wordCount}</span>
                                <span className="text-[9px] font-bold text-slate-300 uppercase tracking-widest">Words</span>
                            </div>
                        </div>

                        <div className="w-[1px] h-6 bg-slate-100" />

                        {/* Dynamic Save Indicator */}
                        <div className="flex items-center gap-3">
                            <div className="flex flex-col items-end">
                                <span className={`text-[9px] font-black uppercase tracking-[0.15em] transition-all duration-300 ${
                                    saveStatus === 'saved' ? 'text-emerald-500' : 
                                    saveStatus === 'saving' ? 'text-indigo-400 animate-pulse' : 'text-amber-400'
                                }`}>
                                    {saveStatus === 'saved' ? 'All changes synced' : 
                                     saveStatus === 'saving' ? 'Auto-saving...' : 'Unsaved changes'}
                                </span>
                                <span className="text-[7px] font-bold text-slate-300 uppercase tracking-widest leading-none mt-0.5">
                                    {saveStatus === 'saved' ? 'Secured in Cloud' : 'Writing...'}
                                </span>
                            </div>
                            <div className={`w-8 h-8 rounded-xl flex items-center justify-center transition-all ${
                                saveStatus === 'saved' ? 'bg-emerald-50 text-emerald-500' : 'bg-slate-50 text-slate-300'
                            }`}>
                                {saveStatus === 'saved' ? <CheckCircle2 size={16} /> : <Cloud size={16} />}
                            </div>
                        </div>
                    </div>

                    <button 
                        onClick={onClose}
                        className="px-8 py-3 bg-[#18216D] text-white font-black text-[10px] uppercase tracking-[0.2em] rounded-2xl hover:bg-[#0D164F] transition-all shadow-xl shadow-indigo-900/10 active:scale-95 group flex items-center gap-3"
                    >
                        <span>Confirm & Exit</span>
                        <Maximize2 size={14} className="opacity-50 group-hover:opacity-100 transition-opacity" />
                    </button>
                </div>
            </header>

            {/* Document Content Area */}
            <main className="flex-1 w-full max-w-5xl mx-auto px-8 pt-16 pb-64">
                <div className="prose-enhanced min-h-[700px] bg-white rounded-[2.5rem] p-16 shadow-2xl shadow-indigo-900/5 border border-slate-100 mb-20 animate-in zoom-in-95 duration-700">
                    <EditorContent editor={editor} />
                </div>
            </main>
            
        </div>,
        document.body
    );
};

export default FocusModeOverlay;
